import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { WP_FAQ_CATEGORIES, getWpCategoryDef } from "@/content/work-permit-corpus/faq-defs";

export const Route = createFileRoute("/work-permit/faq/$category")({
  loader: async ({ params }) => {
    const def = getWpCategoryDef(params.category);
    if (!def) throw notFound();
    const { getWpFaqsByCategoryTh } = await import("@/content/work-permit-corpus");
    return { def, faqs: getWpFaqsByCategoryTh(def.slug) };
  },
  component: WpFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "หมวดคำถามไม่พบ" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleTh} — FAQ ใบอนุญาตทำงาน ${faqs.length} ข้อ`,
        description: def.descTh,
        path: `/work-permit/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/work-permit/faq/${params.category}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ใบอนุญาตทำงาน FAQ", url: `${site}/work-permit/faq` },
              { name: def.titleTh, url: `${site}/work-permit/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/work-permit/faq/${params.category}`)) },
      ],
    };
  },
});

function WpFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/work-permit/faq" className="hover:underline">ใบอนุญาตทำงาน FAQ</a> <span>/</span>{" "}
          <span>{def.titleTh}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{def.titleTh}</h1>
        <p className="mb-6 text-muted-foreground">{def.descTh}</p>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">หมวดอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {WP_FAQ_CATEGORIES.filter((c) => c.slug !== def.slug).map((c) => (
              <a key={c.slug} href={`/work-permit/faq/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.titleTh}
              </a>
            ))}
          </div>
        </section>
        <FaqSourceNote topic="work-permit" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}